// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
/**
 * Composant: WebhooksManagement
 * Interface pour monitorer les webhooks entrants (Stripe, PayPal, etc.)
 */

import React, { useState, useEffect } from 'react';
import {
  Webhook,
  RefreshCw,
  Filter,
  X,
  Eye,
  Calendar,
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Zap,
  ChevronUp,
  Search,
} from 'lucide-react';
import { useWebhooks } from '@/hooks/useWebhooks';
import { WebhookEvent, WebhookEventDetails } from '@/utils/webhooksAPI';
import { Button, Spinner, Badge } from '@/components/ui-v2';

import { useTranslation } from 'react-i18next';
/**
 * Composant principal de gestion des webhooks
 */
export function WebhooksManagement() {
  const { t } = useTranslation();

  const {
    events,
    pagination,
    statistics,
    health,
    loading,
    error,
    loadEvents,
    loadStatistics,
    loadHealth,
    loadEventDetails,
    clearError,
  } = useWebhooks({ autoLoad: true });

  // État des filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    provider: '' | 'paypal' | 'stripe';
    processed: '' | 'true' | 'false';
    page: number;
    limit: number;
  }>({
    provider: '',
    processed: '',
    page: 1,
    limit: 20,
  });

  // Modal détail événement
  const [selectedEvent, setSelectedEvent] = useState<WebhookEventDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer text-sm`;

  /**
   * Charger les données avec les filtres actuels
   */
  const handleLoadData = () => {
    const params: any = { page: filters.page, limit: filters.limit };
    if (filters.provider) params.provider = filters.provider;
    if (filters.processed) params.processed = filters.processed === 'true';

    loadEvents(params);
  };

  /**
   * Appliquer les filtres
   */
  const handleApplyFilters = () => {
    setFilters((prev) => ({ ...prev, page: 1 }));
    handleLoadData();
  };

  /**
   * Réinitialiser les filtres
   */
  const handleResetFilters = () => {
    setFilters({
      provider: '',
      processed: '',
      page: 1,
      limit: 20,
    });
    loadEvents({ page: 1, limit: 20 });
    loadStatistics();
    loadHealth();
  };

  /**
   * Rafraîchir toutes les données
   */
  const handleRefresh = () => {
    handleLoadData();
    loadStatistics();
    loadHealth();
  };

  /**
   * Afficher les détails d'un événement
   */
  const handleViewDetails = async (event: WebhookEvent) => {
    setLoadingDetails(true);
    const details = await loadEventDetails(event.id);
    setLoadingDetails(false);

    if (details) {
      setSelectedEvent(details);
      setShowDetailModal(true);
    }
  };

  /**
   * Changer de page
   */
  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  /**
   * Recharger quand la page change
   */
  useEffect(() => {
    handleLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page]);

  /**
   * Formater une date
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  /**
   * Obtenir le statut d'un événement
   */
  const getEventStatus = (event: WebhookEvent | WebhookEventDetails): 'processed' | 'pending' | 'failed' => {
    if (event.processed) return 'processed';
    if (event.retryCount > 0) return 'failed';
    return 'pending';
  };

  /**
   * Badge de statut
   */
  const getStatusBadge = (status: 'processed' | 'pending' | 'failed') => {
    const config = {
      processed: { variant: 'success' as const, label: t('admin.webhooks.treated') },
      pending: { variant: 'warning' as const, label: t('admin.webhooks.pendingStatus') },
      failed: { variant: 'alert' as const, label: t('admin.webhooks.failed') },
    };
    return <Badge variant={config[status].variant}>{config[status].label}</Badge>;
  };

  /**
   * Badge de provider
   */
  const getProviderBadge = (provider: string) => {
    const config: Record<string, { variant: 'info' | 'pending'; label: string }> = {
      stripe: { variant: 'info', label: 'STRIPE' },
      paypal: { variant: 'info', label: 'PAYPAL' },
    };
    const { variant, label } = config[provider] || { variant: 'pending' as const, label: provider.toUpperCase() };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading && !events.length) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Webhook className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.webhooks.title')}</h1>
            <p className="text-gray-500 mt-0.5">{t('admin.webhooks.subtitle')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={loading}
            icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
          >
            {t('admin.webhooks.refresh')}
          </Button>
          <Button
            variant={showFilters ? 'secondary' : 'ghost'}
            onClick={() => setShowFilters(!showFilters)}
            icon={showFilters ? <ChevronUp className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
          >
            {t('admin.webhooks.filters')}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Zap className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.webhooks.infoTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.webhooks.infoDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50/50 backdrop-blur-sm border border-red-200/50 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-900">{t('common.labels.error')}</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="p-2 text-red-600 hover:bg-red-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Alerte santé webhooks */}
      {health && !health.healthy && (
        <div className="bg-yellow-50/50 backdrop-blur-sm border border-yellow-200/50 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-yellow-100 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900">{t('admin.webhooks.issuesDetected')}</h4>
              <ul className="text-sm text-yellow-800 mt-2 space-y-1">
                {health.issues.map((issue, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Santé système OK */}
      {health && health.healthy && (
        <div className="bg-green-50/50 backdrop-blur-sm border border-green-200/50 rounded-2xl p-5">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-green-100 rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="font-semibold text-green-900">
              {t('admin.webhooks.allSystemsOk')}
            </p>
          </div>
        </div>
      )}

      {/* Panneau de filtres */}
      {showFilters && (
        <div className={cardClasses}>
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('common.actions.filter')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.webhooks.provider')}
              </label>
              <select
                value={filters.provider}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    provider: e.target.value as '' | 'paypal' | 'stripe',
                  }))
                }
                className={selectClasses}
              >
                <option value="">{t('admin.webhooks.allProviders')}</option>
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>

            {/* Statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.webhooks.statusLabel')}
              </label>
              <select
                value={filters.processed}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    processed: e.target.value as '' | 'true' | 'false',
                  }))
                }
                className={selectClasses}
              >
                <option value="">{t('admin.webhooks.allStatuses')}</option>
                <option value="true">{t('admin.webhooks.processed')}</option>
                <option value="false">{t('admin.webhooks.unprocessed')}</option>
              </select>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/30">
            <Button
              variant="secondary"
              onClick={handleApplyFilters}
              disabled={loading}
              icon={<Search className="w-4 h-4" />}
            >
              {t('admin.webhooks.apply')}
            </Button>
            <Button variant="ghost" onClick={handleResetFilters}>
              {t('admin.webhooks.reset')}
            </Button>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Total */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
              </div>
              <div className="p-2 bg-teal-100 rounded-xl">
                <Activity className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </div>

          {/* Traités */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.processedCount')}</p>
                <p className="text-2xl font-bold text-teal-600">{statistics.processed}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>

          {/* En attente */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.pendingCount')}</p>
                <p className="text-2xl font-bold text-yellow-600">{statistics.pending}</p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-xl">
                <Zap className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Échoués */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.failedCount')}</p>
                <p className="text-2xl font-bold text-red-600">{statistics.failed}</p>
              </div>
              <div className="p-2 bg-red-100 rounded-xl">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>

          {/* Taux d'échec */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.failureRate')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statistics.failureRate?.toFixed(1) || '0'}%
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-xl">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table des événements */}
      <div className={`${cardClasses} overflow-hidden p-0`}>
        <div className="px-6 py-4 border-b border-white/30">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Webhook className="w-5 h-5 text-teal-600" />
            {t('admin.webhooks.recentEvents')}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/30 border-b border-white/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.dateTime')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.providerCol')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.eventType')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.statusCol')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.retries')}
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.webhooks.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/20">
              {!events || events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                      <Webhook className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('admin.webhooks.noEvents')}
                    </h3>
                    <p className="text-gray-600">
                      {t('admin.webhooks.noEventsDesc')}
                    </p>
                  </td>
                </tr>
              ) : (
                events.map((event) => {
                  const status = getEventStatus(event);
                  return (
                    <tr key={event.id} className="hover:bg-white/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(event.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getProviderBadge(event.provider)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{event.eventType}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">
                            {event.eventId}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {event.retryCount > 0 ? (
                          <span className="text-red-600 font-medium">{event.retryCount}</span>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleViewDetails(event)}
                          disabled={loadingDetails}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-700 bg-teal-50
                            rounded-xl hover:bg-teal-100 transition-colors disabled:opacity-50"
                        >
                          <Eye className="w-4 h-4" />
                          {t('admin.webhooks.details')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-white/30 border-t border-white/30 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {t('admin.webhooks.page', { current: pagination.page, total: pagination.totalPages, count: pagination.total })}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev || loading}
              >
                {t('admin.webhooks.previous')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext || loading}
              >
                {t('admin.webhooks.next')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal détails de l'événement */}
      {showDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/50">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Webhook className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('admin.webhooks.eventDetails')}</h3>
                    <p className="text-teal-100 text-sm">{selectedEvent.eventType}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Informations générales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.provider')}</p>
                  {getProviderBadge(selectedEvent.provider)}
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.eventType')}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedEvent.eventType}</p>
                </div>
                <div className="col-span-2 bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.eventId')}</p>
                  <p className="text-sm font-mono text-gray-900 break-all">
                    {selectedEvent.eventId}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.creationDate')}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(selectedEvent.createdAt)}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('common.labels.status')}</p>
                  {getStatusBadge(getEventStatus(selectedEvent))}
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.retriesLabel')}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedEvent.retryCount}</p>
                </div>
                {selectedEvent.processedAt && (
                  <div className="bg-gray-50/50 rounded-2xl p-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.webhooks.processDate')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(selectedEvent.processedAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* {t('admin.webhooks.processingError')} */}
              {selectedEvent.processingError && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {t('admin.webhooks.processingError')}
                  </p>
                  <div className="bg-red-50/50 rounded-2xl p-4 border border-red-100">
                    <pre className="text-xs text-red-800 whitespace-pre-wrap overflow-x-auto font-mono">
                      {selectedEvent.processingError}
                    </pre>
                  </div>
                </div>
              )}

              {/* Payload JSON */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                  {t('admin.webhooks.payload')}
                </p>
                <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-x-auto max-h-64 font-mono">
                    {JSON.stringify(selectedEvent.payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Headers */}
              {selectedEvent.headers && Object.keys(selectedEvent.headers).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {t('admin.webhooks.headers')}
                  </p>
                  <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-x-auto font-mono">
                      {JSON.stringify(selectedEvent.headers, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Footer modal */}
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200">
              <Button variant="ghost" onClick={() => setShowDetailModal(false)} className="w-full">
                {t('admin.webhooks.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
